/**
 * Addresses the hosting layer answers itself (a Cloud Function or a static
 * page), so the app-shell worker must NOT answer them with index.html.
 * The list comes from firebase.json (see scripts/service-worker/derive-server-paths.cjs
 * -> src/generated/serverHandledPaths.json); nothing here is typed by hand.
 *
 * Scope: only firebase.json REWRITES are covered. Plain static files served from
 * client/public (e.g. /test-embed.html) are not rewrites and are not listed.
 */
const RESERVED = /^\/__\//; // Firebase auth handler + reserved paths

// Firebase glob syntax beyond `*` / `**` (?, {a,b}, @(a|b), [ab], !(x)) is not
// supported: throwing fails the build instead of silently denying a literal.
const UNSUPPORTED = /[?{}@!()[\]]/;

const escape = (s: string): string => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

/** firebase.json glob ("/p/**", "/pay", "/forms/contact.html") -> anchored RegExp.
 *  NavigationRoute tests pathname + search, so a source also matches a query string.
 *  Over-denying is harmless (the request goes to hosting, which falls back to the
 *  app shell for non-server addresses), so `/pay/` and a bare `/p` are denied too. */
export function globToDenyRegExp(source: string): RegExp {
  if (UNSUPPORTED.test(source)) {
    throw new Error(`Unsupported firebase.json source glob for the worker denylist: ${source}`);
  }
  const deep = source.endsWith('/**');
  // Leading and trailing slashes are tolerated in any number: the browser keeps
  // `//pay` in the pathname the worker tests (Nick's invoice link has two), while
  // hosting repairs it with a 307, but only if the worker lets the request through.
  const base = (deep ? source.slice(0, -3) : source).replace(/^\/+/, '').replace(/\/+$/, '');
  const body = base
    .split('**')
    .map((part) => part.split('*').map(escape).join('[^/?#]*'))
    .join('.*');
  return new RegExp(`^/+${body}${deep ? '(?:/.*)?' : '/*'}(?:[?#].*)?$`);
}

export function buildNavigationDenylist(serverSources: readonly string[]): RegExp[] {
  return [RESERVED, ...serverSources.map(globToDenyRegExp)];
}
